'use client';

import { useEffect, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { trackGA4 } from './ga-events';

declare global {
  interface Window {
    fbq?: (action: string, eventName: string, params?: any) => void;
  }
}

const VISITOR_COOKIE = '_ls_visitor';
const VISITOR_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds
const SESSION_STORAGE_KEY = '_ls_session';

function getBodyDatasetValue(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const value = document.body?.dataset?.[name];
  return value || undefined;
}

function getOrCreateVisitorId(): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp(`(?:^|; )${VISITOR_COOKIE}=([^;]+)`));
  if (match) return decodeURIComponent(match[1]);
  const id = crypto.randomUUID();
  document.cookie = `${VISITOR_COOKIE}=${encodeURIComponent(id)}; max-age=${VISITOR_COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
  return id;
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;

  const sessionId = crypto.randomUUID();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  return sessionId;
}

function resolveStoreId(explicitStoreId?: string): string {
  return explicitStoreId || getBodyDatasetValue('storeId') || '';
}

function resolveCollectorBase(): string {
  return (
    process.env.NEXT_PUBLIC_LAUNCHSTORE_API_URL ||
    getBodyDatasetValue('launchstoreApiUrl') ||
    'http://localhost:3000/api'
  ).replace(/\/$/, '');
}

interface UsePixelTrackingOptions {
  storeId?: string;
  enabled?: boolean;
}

function mapEventNameToCollectorType(eventName: string): string {
  const mapping: Record<string, string> = {
    PageView: 'page_view',
    ViewContent: 'product_view',
    Search: 'search',
    AddToCart: 'add_to_cart',
    InitiateCheckout: 'checkout_started',
    Purchase: 'purchase_completed',
  };

  return mapping[eventName] || 'page_view';
}

function buildEventId(storeId: string, eventName: string, params: any): string {
  const orderId = params?.orderId || params?.order_id;
  const cartId = params?.cartId || params?.cart_id;

  if (eventName === 'Purchase' && orderId) {
    return `order:${orderId}:purchase_completed`;
  }

  if (eventName === 'InitiateCheckout' && cartId) {
    return `cart:${cartId}:checkout_started`;
  }

  const stableTarget =
    orderId ||
    cartId ||
    params?.content_ids?.[0] ||
    params?.content_id ||
    'na';

  const seed = `${storeId}:${eventName}:${stableTarget}:${Date.now()}:${Math.random()}`;
  return `ls_${btoa(seed).replace(/=/g, '').slice(0, 64)}`;
}

export function usePixelTracking({ storeId, enabled = true }: UsePixelTrackingOptions) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const effectiveStoreId = resolveStoreId(storeId);

  // Extract UTM parameters from URL
  const getUTMParams = useCallback(() => {
    if (typeof window === 'undefined') return {};
    
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_content: params.get('utm_content') || undefined,
      utm_term: params.get('utm_term') || undefined,
      fbclid: params.get('fbclid') || undefined,
    };
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !effectiveStoreId) {
      return;
    }

    if (window.fbq) {
      window.fbq('track', 'PageView');
    }

    void sendServerEvent(effectiveStoreId, 'PageView', {
      path: pathname,
      search: searchParams?.toString() || undefined,
    });
  }, [pathname, searchParams, enabled, effectiveStoreId]);

  // Track custom events
  const trackEvent = useCallback((eventName: string, params?: any) => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    try {
      // Include UTM parameters in event data
      const utmParams = getUTMParams();
      const enrichedParams = { ...params, ...utmParams };

      if (window.fbq) {
        window.fbq('track', eventName, enrichedParams);
      }

      // Fan out to GA4 when configured (no-op when gtag is absent)
      trackGA4(eventName, enrichedParams);

      if (effectiveStoreId) {
        void sendServerEvent(effectiveStoreId, eventName, enrichedParams);
      }
    } catch (error) {
      console.error('Error tracking Facebook Pixel event:', error);
    }
  }, [effectiveStoreId, enabled, getUTMParams]);

  return { trackEvent };
}

// Send event to backend for server-side Conversions API
async function sendServerEvent(storeId: string, eventName: string, params: any) {
  try {
    const collectorBase = resolveCollectorBase();

    if (!storeId || !collectorBase) {
      return;
    }

    const eventType = mapEventNameToCollectorType(eventName);
    const eventId = buildEventId(storeId, eventName, params);
    const visitorId = getOrCreateVisitorId();
    const sessionId = params?.session_id || getOrCreateSessionId();

    await fetch(`${collectorBase}/api/analytics/collect/${storeId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventType,
        eventId,
        visitorId: visitorId || undefined,
        sessionId: sessionId || undefined,
        userId: params?.user_id,
        productId: params?.content_ids?.[0] || params?.content_id,
        cartId: params?.cart_id,
        orderId: params?.order_id,
        searchQuery: params?.search_string,
        value: params?.value,
        currency: params?.currency,
        quantity: params?.num_items,
        sourceUrl: window.location.href,
        referrer: document.referrer || undefined,
        utmSource: params?.utm_source,
        utmMedium: params?.utm_medium,
        utmCampaign: params?.utm_campaign,
        utmContent: params?.utm_content,
        utmTerm: params?.utm_term,
        fbclid: params?.fbclid,
        metadata: params,
      }),
    });
  } catch (error) {
    console.error('Error sending server-side pixel event:', error);
  }
}

// Specialized hooks for common events

export function useProductView(storeId?: string) {
  const { trackEvent } = usePixelTracking({ storeId });

  const trackProductView = useCallback((product: {
    id: string;
    title: string;
    price?: number;
    currency?: string;
    handle?: string;
    customerEmail?: string;
    customerFirstName?: string;
  }) => {
    trackEvent('ViewContent', {
      content_ids: [product.id],
      content_type: 'product',
      content_name: product.title,
      value: product.price,
      currency: product.currency || 'USD',
      // Passed through metadata for browse-abandonment wiring
      customerEmail: product.customerEmail,
      customerFirstName: product.customerFirstName,
      productTitle: product.title,
      productHandle: product.handle,
    });
  }, [trackEvent]);

  return { trackProductView };
}

export function useAddToCart(storeId?: string) {
  const { trackEvent } = usePixelTracking({ storeId });

  const trackAddToCart = useCallback((item: {
    variantId: string;
    productTitle: string;
    quantity: number;
    price: number;
    currency?: string;
    cartId?: string;
  }) => {
    trackEvent('AddToCart', {
      content_ids: [item.variantId],
      content_type: 'product',
      content_name: item.productTitle,
      value: item.price * item.quantity,
      currency: item.currency || 'USD',
      num_items: item.quantity,
      cart_id: item.cartId,
    });
  }, [trackEvent]);

  return { trackAddToCart };
}

export function useInitiateCheckout(storeId?: string) {
  const { trackEvent } = usePixelTracking({ storeId });

  const trackInitiateCheckout = useCallback((cart: {
    items: Array<{ variant_id: string }>;
    total: number;
    currency_code: string;
    id?: string;
  }) => {
    trackEvent('InitiateCheckout', {
      content_ids: cart.items.map(item => item.variant_id),
      num_items: cart.items.length,
      value: cart.total,
      currency: cart.currency_code.toUpperCase(),
      cart_id: cart.id,
    });
  }, [trackEvent]);

  return { trackInitiateCheckout };
}

export function usePurchase(storeId?: string) {
  const { trackEvent } = usePixelTracking({ storeId });

  const trackPurchase = useCallback((order: {
    id: string;
    items: Array<{ variant_id: string }>;
    total: number;
    currency_code: string;
  }) => {
    trackEvent('Purchase', {
      content_ids: order.items.map(item => item.variant_id),
      content_type: 'product',
      value: order.total,
      currency: order.currency_code.toUpperCase(),
      num_items: order.items.length,
      order_id: order.id,
    });
  }, [trackEvent]);

  return { trackPurchase };
}

export function useSearch(storeId?: string) {
  const { trackEvent } = usePixelTracking({ storeId });

  const trackSearch = useCallback((searchQuery: string) => {
    trackEvent('Search', {
      search_string: searchQuery,
      content_category: 'products',
    });
  }, [trackEvent]);

  return { trackSearch };
}
